import urlParse from 'url-parse';
import cuid from 'cuid';
import {initConversionPixel} from '../../conversionpixel';
import {BTTrackerBase} from './base';
import {TOPIC_BIOTRUST_OFFER_ID, TOPIC_EXPERIMENT_ID, TOPIC_TATARI_SESSION_ID} from '../messagehub/topics';
import {EventTypes} from '../events';
import BTCookies from '../../common/cookies';
import {OFFER_ID_COOKIE_NAME, OFFER_ID_URL_PARAM} from '../constants';

export class CustomBTTracker extends BTTrackerBase {
  constructor(args) {
    super(args);
    this.events = [EventTypes.OFFER_ID, EventTypes.IP_ADDRESS_INFO, EventTypes.ADD_TO_CART];
  }

  getName() {
    return 'CustomBTTracker';
  }

  get order() {
    return 0;
  }

  configure({statsUrl, conversionpixelJS, sessionCookieName, conversionPixelFindOfferId, ...params}) {
    super.configure({statsUrl, conversionpixelJS, conversionPixelFindOfferId, ...params});

    this.statsUrl = statsUrl;
    this.conversionpixelJS = conversionpixelJS;
    this.sessionCookieName = sessionCookieName;
    this.conversionPixelFindOfferId = conversionPixelFindOfferId;

    if (this.conversionpixelJS) {
      this.enable();
      this.log('Tracker is configured.');
    } else {
      this.disable();
      this.log('CustomBTTracker disabled because conversionpixelJS is not defined.');
    }
  }

  init({fallbackSessionId, fallbackAgentId, ...restParams} = {}) {
    this.loadConversionPixelJS(fallbackSessionId, fallbackAgentId);
    this.messageHub.subscribe({topic: TOPIC_EXPERIMENT_ID, handler: this.handleOptimizeExperiment});
    this.messageHub.subscribe({topic: TOPIC_TATARI_SESSION_ID, handler: this.handleTatariSessionId});

    super.init(restParams);
  }

  loadConversionPixelJS(fallbackSessionId, fallbackAgentId) {
    window.btDataLayer = window.btDataLayer || [];
    // noinspection SpellCheckingInspection
    const {sessionId, agentId} = initConversionPixel({
      statsUrl: this.statsUrl,
      baseDomain: window.location.hostname.split('.').slice(-2).join('.'),
      sessionCookieName: this.sessionCookieName,
      globalFunctionName: 'btag',
      fallbackSessionId,
      fallbackAgentId,
    });

    this.sessionId = sessionId;
    this.agentId = agentId;
  }

  purchase(order, items) {
    if (!super.purchase(order, items)) {
      return false;
    }

    try {
      const params = {
        orderId: order.id,
        through: order.source,
        amount: order.subtotal,
        properties: {
          token: order.token,
          currency: 'USD',
          realOrderId: order.shopifyOrderId,
          position: order.step,
          funnelId: order.funnelId,
          partialId: order.partialId,
          pageType: order.pageType,
          eventSource: order.eventSource,
          uuid: (order.uuid || '').trim(),
          /* eslint-disable camelcase */
          lineItems: items.map(
            item => {
              return {
                product_id: item.productId,
                variant_id: item.id,
                price: item.price,
                discounted_price: (item.price - item.discount).toFixed(2),
                quantity: item.quantity,
              };
            },
          ),
          /* eslint-enable camelcase */
        },
      };

      this.log(`btag purchase sent ${order.id}-${order.step}`, params);
      window.btag('purchase', `${order.id}-${order.step}`, params);

      // Do not remove the override offer id cookie on purchase.
      // Preserve it so other orders created on this session will be attributed to the same offer id
      // this._removeOfferIdCookie();

      return true;
    } catch (err) {
      return false;
    }
  }

  view({url} = {}) {
    if (!super.view({url})) {
      return false;
    }

    window.btag('pageview', {url});
    this.log('Stats view pixel');
    if (this.conversionPixelFindOfferId) {
      this._capturePageOfferId();
    }
    return true;
  }

  event(type, args = {}) {
    if (!super.event(type, args)) {
      return false;
    }

    switch (type) {
      case EventTypes.IP_ADDRESS_INFO: {
        const {ipInfo} = args;
        if (ipInfo) {
          window.btag(
            'event',
            `ipinfo-${new Date().getTime().toFixed(0)}`,
            {
              EventCategory: 'ipinfo',
              Properties: {...ipInfo},
            },
          );
        }
        break;
      }
      case EventTypes.OFFER_ID: {
        const {offerId} = args;
        if (!offerId) {
          return false;
        }
        this._trackOfferId({offerId, url: window.location.href});
        break;
      }
      case EventTypes.ADD_TO_CART: {
        const url = urlParse(window.location.href, true);
        const props = {
          url: url.href,
          path: url.pathname,
          query: url.query,
          ...args,
        };
        window.btag('event', `addToCart-${cuid()}`, {
          EventCategory: 'addToCart',
          Properties: props,
        });
        break;
      }
      default:
        console.error(`Unsupported Event Type: ${type}`, args);
        break;
    }

    return true;
  }

  handleOptimizeExperiment({experimentId, experimentVariation}) {
    // create session event with Experiment Id/Variation
    window.btag(
      'event',
      `${experimentId}.${experimentVariation}`,
      {
        EventCategory: 'experiment',
        Properties: {
          ExperimentId: experimentId,
          ExperimentVariant: experimentVariation,
        },
      },
    );

    // store the experiment id/variation in localStorage for reference on other pages,
    // which allows us to change pages later on in the funnel based on what test the visitor has seen (e.g. MAW)
    window.localStorage.setItem(experimentId, experimentVariation);
  }

  handleTatariSessionId({tatariSessionId}) {
    window.btag(
      'event',
      `tatariSessionLoaded-${cuid()}`,
      {
        EventCategory: 'tatari-session',
        Properties: {TatariSessionId: tatariSessionId},
      },
    );
  }

  _trackOfferId({offerId, url}) {
    window.btag('event', url, {EventCategory: 'offer-view', Properties: {OfferId: offerId} });
    this.log(`Offer Id ${offerId} sent to conversionpixel for location ${url}`);
  }

  /**
   * Check if the URL parameter for offer ID exists
   * @returns {string|null} The offer ID from URL parameter or null if not found
   */
  _getOfferIdFromUrlParam() {
    const url = urlParse(window.location.href, true);
    const offerIdParam = url.query[OFFER_ID_URL_PARAM];

    // only process offer ids in the 9000-9999 range
    if (offerIdParam && /^9\d{3}$/.test(offerIdParam)) {
      this.log(`Found offer ID in URL parameter: ${offerIdParam}`);
      return offerIdParam;
    }

    return null;
  }

  /**
   * Store the offer ID in a cookie with the same expiration as the session cookie
   * @param {string} offerId The offer ID to store
   */
  _storeOfferIdInCookie(offerId) {
    // Calculate expiration time (4 hours from now, same as session cookie)
    const expirationTime = new Date(new Date().getTime() + 240 * 60 * 1000);

    // Get the base domain from the current hostname
    const baseDomain = window.location.hostname.split('.').slice(-2).join('.');

    // Store the offer ID in a cookie
    BTCookies.set(
      OFFER_ID_COOKIE_NAME,
      offerId,
      {domain: baseDomain, expires: expirationTime},
    );

    this.log(`Stored offer ID ${offerId} in cookie until ${expirationTime.toTimeString()}`);
  }

  /**
   * Get the offer ID from the cookie
   * @returns {string|null} The offer ID from the cookie or null if not found
   */
  _getOfferIdFromCookie() {
    const offerId = BTCookies.get(OFFER_ID_COOKIE_NAME);

    if (offerId) {
      this.log(`Found offer ID in cookie: ${offerId}`);
    }

    return offerId || null;
  }

  /**
   * Check if the current URL contains /pages/
   * @returns {boolean} True if the URL contains /pages/, false otherwise
   */
  _isStorefrontUrl() {
    return !window.location.pathname.includes('/pages/');
  }

  _capturePageOfferId() {
    const captureOfferId = () => {
      const pageOfferId = this._getOfferIdFromPageContent();

      // only send the offer id when it is found on the page. This prevents sending the
      // override offer id, which is stored in a cookie, on the post-purchase/TY page
      if (pageOfferId) {
        const offerId = this._getOfferIdFromOverrideSources() || pageOfferId;
        this._trackOfferId({offerId, url: window.location.href});
        this.messageHub.publish({topic: TOPIC_BIOTRUST_OFFER_ID, message: {offerId} });
      }
    };

    if (window.document.readyState === 'loading') {
      window.document.addEventListener('DOMContentLoaded', captureOfferId);
    } else {
      captureOfferId();
    }
  }

  /**
   * Get offer ID from various sources in priority order
   * @returns {string|null} The offer ID from the highest priority source or null if not found
   */
  _getOfferIdFromOverrideSources() {
    if (this._isStorefrontUrl()) {
      const urlParamOfferId = this._getOfferIdFromUrlParam();
      if (urlParamOfferId) {
        this._storeOfferIdInCookie(urlParamOfferId);
        return urlParamOfferId;
      }

      const cookieOfferId = this._getOfferIdFromCookie();
      if (cookieOfferId) {
        this._storeOfferIdInCookie(cookieOfferId); // update cookie expiration
        return cookieOfferId;
      }
    }

    return null;
  }

  /**
   * Extract offer ID from page content
   * @returns {string|null} The offer ID from page content or null if not found
   */
  _getOfferIdFromPageContent() {
    if (!window.$ || !window.$.fn || !window.$.fn.jquery) {
      return null;
    }

    console.debug('Detected jQuery', {version: $.fn.jquery});
    const offerIdPattern = new RegExp(/offer(?:\s*|_)id\s*(?::|\s+)\s*(\d+)/i);
    let offerId = null;

    const footerMatch = offerIdPattern.exec($('footer').text());
    if (footerMatch) {
      // eslint-disable-next-line prefer-destructuring
      offerId = footerMatch[1];
      this.log(`Detected Offer ID in footer: ${offerId}`);
      return offerId;
    }

    const policyMatch = offerIdPattern.exec($('.policy').text());
    if (policyMatch) {
      // eslint-disable-next-line prefer-destructuring
      offerId = policyMatch[1];
      this.log(`Detected Offer ID in policy section: ${offerId}`);
      return offerId;
    }

    let fieldOfferId = null;
    $('input[name=offer_id]').each((idx, el) => {
      fieldOfferId = $(el).val();
      if (fieldOfferId) {
        this.log(`Offer ID detected from hidden input field: ${fieldOfferId}`, this);
        return false; // break the loop
      }
      return true; // continue search
    });
    if (fieldOfferId) {
      return fieldOfferId;
    }

    // Last resort -- search the entire page
    const bodyMatch = offerIdPattern.exec($('body').html());
    if (bodyMatch) {
      // eslint-disable-next-line prefer-destructuring
      offerId = bodyMatch[1];
      this.log(`Offer ID detected within HTML on the page: ${offerId}`);
      return offerId;
    }

    return null;
  }
}
